import axios, { AxiosError } from "axios";
import { UserConsent, UserData } from "../model/User";
import { Partner } from "../services/partnerService";
import { Interpreter } from "./partnerInterpreterService";
import { Base64 } from "js-base64";
import { datadogRum } from "@datadog/browser-rum";
import { DateTime } from "luxon";

interface JwtSession {
  token: string;
  patientId: number;
  transactionId?: number;
  appointmentId?: number;
  hospitalId?: number;
  expiresAt: string;
  transactionStatus: number;
  transactionProgress: number;
  callSessionId: string;
  callAccessToken: string;
  callUserId: string;
  callPatientName: string;
  transactionCode: string;
}

// Legacy Session interface removed - JWT-only implementation

interface CheckQueueApiResponse {
  TransactionId: number;
  TransactionCode: string;
  TransactionSessionId: string;

  TransactionStatus: number;
  TransactionStatusText: string;
  TransactionTimeLeft: number;
  IsReview: boolean;
  TransactionSource: string;

  PatientId: number;
  PatientName: string;
  PatientImage: string;

  NurseId: number;
  NurseName: string;
  NurseImage: string;
  NurseSpecialty: string;

  DoctorId: number;
  DoctorName: string;
  DoctorImage: string;
  DoctorSpecialty: string;

  PharmacyId: number;
  PharmacyName: string;
  PharmacyImage: string;
  PharmacySpecialty: string;

  CallVendor: string;
  CallSessionId: string;
  CallAccessToken: string;
  CallUserId: string;
  PatientJoinUrl?: string;
  IsHuawei: boolean;
}

interface WebSocketQueueData {
  transactionCode: string;
  transactionId: number;
  hasWebSocketData: boolean;
}

const transactionService = {
  // initSession method removed - GetSession endpoint no longer exists
  // Registration now handles session creation via JWT tokens

  // Legacy session methods removed - JWT-only implementation

  // JWT Session Management
  getJwtSession: (): JwtSession | null => {
    const sessionBase64 = localStorage.getItem("SvhJwtSession");

    if (!sessionBase64) {
      return null;
    }

    try {
      const sessionString = Base64.decode(sessionBase64);
      const session = JSON.parse(sessionString) as JwtSession;

      // Check if token is expired
      if (new Date(session.expiresAt) <= new Date()) {
        console.log("JWT token expired, clearing session");
        localStorage.removeItem("SvhJwtSession");
        return null;
      }

      return session;
    } catch (error) {
      console.error("Error parsing JWT session:", error);
      localStorage.removeItem("SvhJwtSession");
      return null;
    }
  },

  setJwtSession: (session: JwtSession) => {
    const sessionBase64 = Base64.encode(JSON.stringify(session));
    localStorage.setItem("SvhJwtSession", sessionBase64);
  },

  getAuthHeaders: (contentType: string = "application/json") => {
    const jwtSession = transactionService.getJwtSession();
    if (!jwtSession) {
      throw new Error("No valid JWT session found. Please register first.");
    }

    return {
      Authorization: `Bearer ${jwtSession.token}`,
      "Content-Type": contentType,
    };
  },

  getAuthHeadersOnly: () => {
    const jwtSession = transactionService.getJwtSession();
    if (!jwtSession) {
      throw new Error("No valid JWT session found. Please register first.");
    }

    return {
      Authorization: `Bearer ${jwtSession.token}`,
    };
  },

  clearJwtSession: () => {
    localStorage.removeItem("SvhJwtSession");
  },

  // Legacy CK/SK methods removed - JWT-only implementation

  // Get transaction status from JWT session
  getTransactionStatus: () => {
    const jwtSession = transactionService.getJwtSession();
    return jwtSession?.transactionStatus ?? 0;
  },

  // Get transaction progress from JWT session
  getTransactionProgress: () => {
    const jwtSession = transactionService.getJwtSession();
    return jwtSession?.transactionProgress ?? 0;
  },

  // Get transaction code from JWT session
  getTransactionCode: () => {
    const jwtSession = transactionService.getJwtSession();
    return jwtSession?.transactionCode ?? "";
  },

  // Get patient ID from JWT session
  getPatientId: () => {
    const jwtSession = transactionService.getJwtSession();
    return jwtSession?.patientId;
  },

  // Get transaction ID from JWT session
  getTransactionId: () => {
    const jwtSession = transactionService.getJwtSession();
    return jwtSession?.transactionId;
  },

  // Get WebSocket queue data for establishing connection
  getWebSocketQueueData: (): WebSocketQueueData | null => {
    const jwtSession = transactionService.getJwtSession();
    if (!jwtSession || !jwtSession.transactionCode) {
      return null;
    }

    return {
      transactionCode: jwtSession.transactionCode,
      transactionId: jwtSession.transactionId || 0,
      hasWebSocketData: !!jwtSession.transactionCode,
    };
  },

  // Helper function to check if user data has changed
  hasUserDataChanged: (userData: UserData): boolean => {
    try {
      // Get the current JWT session
      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) return true; // No session means data has changed

      // Try to get the last registered user data from localStorage
      const lastUserDataStr = localStorage.getItem("lastRegisteredUserData");
      if (!lastUserDataStr) return true; // No previous data means data has changed

      // Check if the last registered data is too old (more than 2 hours)
      const lastRegisteredTime = localStorage.getItem("lastRegisteredTime");
      if (lastRegisteredTime) {
        const registeredAt = DateTime.fromISO(lastRegisteredTime);
        const now = DateTime.local();
        const diff = now.diff(registeredAt, "hours");

        // If more than 2 hours have passed, consider it as changed data
        // This matches the partner data expiration logic in partnerService.ts
        if (diff.hours >= 2) {
          console.log(
            "Last registration was more than 2 hours ago, treating as new user"
          );
          return true;
        }
      }

      const lastUserData = JSON.parse(lastUserDataStr);

      // Compare key fields
      const hasFirstNameChanged = userData.firstName !== lastUserData.firstName;
      const hasLastNameChanged = userData.lastName !== lastUserData.lastName;
      const hasIdCardChanged = userData.idCard !== lastUserData.idCard;
      const hasBirthdateChanged = userData.birthdate !== lastUserData.birthdate;
      const hasTelChanged = userData.tel !== lastUserData.tel;
      const hasEmailChanged = userData.email !== lastUserData.email;
      const hasHnChanged = userData.hn !== lastUserData.hn;

      const hasChanged =
        hasFirstNameChanged ||
        hasLastNameChanged ||
        hasIdCardChanged ||
        hasBirthdateChanged ||
        hasTelChanged ||
        hasEmailChanged ||
        hasHnChanged;

      if (hasChanged) {
        console.log(
          "User data changes detected:",
          hasFirstNameChanged ? "FirstName changed" : "",
          hasLastNameChanged ? "LastName changed" : "",
          hasIdCardChanged ? "IDCard changed" : "",
          hasBirthdateChanged ? "Birthdate changed" : "",
          hasTelChanged ? "Tel changed" : "",
          hasEmailChanged ? "Email changed" : "",
          hasHnChanged ? "HN changed" : ""
        );
      }

      return hasChanged;
    } catch (error) {
      console.error("Error checking if user data changed:", error);
      return true; // If there's an error, assume data has changed
    }
  },

  // Register patient with JWT authentication
  register: async (
    userData: UserData,
    userConsent: UserConsent,
    partnerName: string,
    contactName?: string,
    isGuardianMode: boolean = false,
    hospitalId?: number
  ) => {
    // Check if user data has changed
    const hasDataChanged = transactionService.hasUserDataChanged(userData);

    // If user data has changed, clear both sessions to force creation of a new one
    if (hasDataChanged) {
      console.log(
        "User data changed, clearing all sessions to create a new one"
      );
      transactionService.clearJwtSession();
      localStorage.removeItem("SvhSession"); // Remove legacy session
    } else {
      console.log("User data unchanged, checking existing JWT session");
      const existingSession = transactionService.getJwtSession();
      if (existingSession) {
        console.log("Valid JWT session exists, skipping registration");
        return;
      }
    }

    // Create JSON request with camelCase fields
    const requestData: any = {
      hnNumber: userData.hn,
      firstName: userData.firstName,
      lastName: userData.lastName,
      birthDate: userData.birthdate,
      idCard: userData.idCard,
      tel: userData.tel,
      email: userData.email,
      gender: userData.gender,
      agreementName: "UsageTerms",
      contact: contactName || partnerName || "Webview",
      isAcceptTerms: userConsent.isAcceptTerms,
      isConsent3rdParty: userConsent.isConsent3rdParty,
      isConsentMarketing: userConsent.isConsentMarketing,
      isGuardianMode: isGuardianMode,
      hospitalName: partnerName,
    };

    // Add hospitalId for WellSuperApp and Well_AIDOCTOR
    if (
      (partnerName === "WellSuperApp" || partnerName === "Well_AIDOCTOR") &&
      hospitalId
    ) {
      requestData.hospitalId = hospitalId.toString();
    }

    // Add accessToken and idToken for Well_AIDOCTOR from decrypted token payload
    if (partnerName === "Well_AIDOCTOR") {
      if (userData.accessToken) {
        requestData.accessToken = userData.accessToken;
      }
      if (userData.idToken) {
        requestData.idToken = userData.idToken;
      }
    }

    try {
      const response = await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/Register",
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Handle JWT response (camelCase from backend)
      const { token, patientId, status, expiresAt } = response.data;

      // Create JWT session
      const jwtSession: JwtSession = {
        token,
        patientId,
        expiresAt,
        transactionStatus: 0,
        transactionProgress: 0,
        callSessionId: "",
        callAccessToken: "",
        callUserId: "",
        callPatientName: "",
        transactionCode: "",
      };

      // Store JWT session
      transactionService.setJwtSession(jwtSession);

      // Store the current user data for future comparison
      localStorage.setItem("lastRegisteredUserData", JSON.stringify(userData));

      // Store the registration time
      const isoTime = DateTime.local().toISO();
      localStorage.setItem(
        "lastRegisteredTime",
        isoTime || new Date().toISOString()
      );

      // Clear the 'callEnded' flag after successful registration
      localStorage.removeItem("callEnded");

      console.log("Registration successful with JWT:", { patientId, status });
    } catch (error) {
      console.error("Registration failed", error);
      throw error; // Re-throw the error so the calling function can handle it
    }
  },

  // Register appointment with JWT authentication
  registerAppointment: async (
    appointmentCode: string,
    appointmentPassword: string,
    partnerName: string
  ) => {
    try {
      // Create JSON request with camelCase fields
      const requestData = {
        code: appointmentCode,
        password: appointmentPassword,
      };

      const response = await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/RegisterAppointment",
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Handle JWT response (camelCase from backend)
      const { token, patientId, appointmentId, status, expiresAt } =
        response.data;

      // Decode JWT payload to extract hospitalId
      let hospitalId: number | undefined;
      try {
        const payloadBase64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(payloadBase64));
        hospitalId = payload.hospitalId;
      } catch {
        console.warn("[registerAppointment] Could not decode JWT payload for hospitalId");
      }

      // Create JWT session with appointment context
      const jwtSession: JwtSession = {
        token,
        patientId,
        appointmentId,
        hospitalId,
        expiresAt,
        transactionStatus: 0,
        transactionProgress: 0,
        callSessionId: "",
        callAccessToken: "",
        callUserId: "",
        callPatientName: "",
        transactionCode: "",
      };

      // Store JWT session
      transactionService.setJwtSession(jwtSession);

      console.log("Appointment registration successful with JWT:", {
        patientId,
        appointmentId,
        hospitalId,
        status,
      });
      return { status: response.status, hospitalId };
    } catch (e) {
      console.error("RegisterAppointmentFailed", e);
      return { status: (e as AxiosError).response!.status, hospitalId: undefined };
    }
  },

  // Create queue with JWT authentication
  createQueue: async (
    partner: Partner,
    source?: string,
    partnerConsultId?: string
  ) => {
    try {
      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      // Create JSON request with camelCase fields
      const requestData: any = {
        patientId: jwtSession.patientId,
        appointmentId: jwtSession.appointmentId, // Include if available from appointment flow
      };

      if (source) {
        requestData.source = source;
      }

      // Pass interpreter language explicitly so backend can set InterpreterLanguageId
      if (
        partner?.name &&
        partner.name.toLowerCase().trim() === "interpreter"
      ) {
        const interpreterPartner = partner as Interpreter;
        requestData.interpreterLanguage =
          interpreterPartner.interpreterLanguage || "";
      }

      const response = await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/CreateQueue",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      // Handle camelCase response from backend
      const {
        transactionId,
        transactionCode,
        partner: responsePartner,
        accessToken,
        partnerConsultId: responseConsultId,
        message,
      } = response.data;

      // Update JWT session with transaction context
      jwtSession.transactionId = transactionId;
      jwtSession.transactionCode = transactionCode;
      transactionService.setJwtSession(jwtSession);

      console.log("CreateQueue successful:", {
        transactionId,
        transactionCode,
        message,
      });
      return {
        transactionId,
        transactionCode,
        accessToken,
        partnerConsultId: responseConsultId,
        isExistingTransaction: message === "existing_transaction",
      };
    } catch (error) {
      console.error("CreateQueueFailed", error);
      throw error;
    }
  },

  // Check queue with JWT authentication
  checkQueue: async () => {
    try {
      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      if (!jwtSession.transactionId) {
        throw new Error("No transaction ID found. Please create queue first.");
      }

      // Create JSON request with transaction ownership
      const requestData = {
        transactionId: jwtSession.transactionId,
      };

      const response: any = await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/CheckQueue",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      // Note: Backend still returns PascalCase for CheckQueue (legacy compatibility)
      const data = response.data as CheckQueueApiResponse;

      // Update JWT session with call information
      jwtSession.callAccessToken = data.CallAccessToken;
      jwtSession.callSessionId = data.CallSessionId;
      jwtSession.callUserId = data.CallUserId;
      jwtSession.callPatientName = data.PatientName;
      jwtSession.transactionCode = data.TransactionCode;

      jwtSession.transactionStatus = data.TransactionStatus;
      if (data.TransactionStatus > jwtSession.transactionProgress) {
        jwtSession.transactionProgress = data.TransactionStatus;
      }
      transactionService.setJwtSession(jwtSession);

      datadogRum.setUser({
        id: data.PatientId.toString(),
        name: data.PatientName,
      });

      console.log("CheckQueue successful:", {
        transactionId: jwtSession.transactionId,
        status: data.TransactionStatus,
      });
      return data;
    } catch (e) {
      console.error("CheckQueueFailed", e);
      throw e;
    }
  },

  // Cancel queue with JWT authentication
  cancelQueue: async () => {
    try {
      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      if (!jwtSession.transactionId) {
        throw new Error("No transaction ID found. Please create queue first.");
      }

      // Create JSON request with transaction ownership
      const requestData = {
        transactionId: jwtSession.transactionId,
        reason: "User cancelled",
      };

      await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/CancelQueue",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      console.log("CancelQueue successful:", {
        transactionId: jwtSession.transactionId,
      });
    } catch (e) {
      console.error("CancelQueueFailed", e);
      throw e;
    }
  },

  // Rate transaction with JWT authentication
  rateTransaction: async (
    satisfactionRating: number,
    recommendationRating: number,
    comment: string
  ) => {
    try {
      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      if (!jwtSession.transactionId) {
        throw new Error("No transaction ID found. Please create queue first.");
      }

      // Create JSON request with camelCase fields
      const requestData = {
        transactionId: jwtSession.transactionId,
        rating: satisfactionRating,
        type: "satisfaction",
        comment: comment,
        transactionCode: jwtSession.transactionCode,
      };

      await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/Rate",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      console.log("RateTransaction successful:", {
        transactionId: jwtSession.transactionId,
        rating: satisfactionRating,
      });
    } catch (e) {
      console.error("RateTransactionFailed", e);
      throw e;
    }
  },

  // Request callback with JWT authentication
  requestCallback: async (symptom: string) => {
    try {
      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      if (!jwtSession.transactionId) {
        throw new Error("No transaction ID found. Please create queue first.");
      }

      // Send both transactionId and symptom (transaction may be cancelled)
      const requestData = {
        transactionId: jwtSession.transactionId,
        symptom: symptom,
      };

      await axios.post(
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/RequestCallBack",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      console.log(
        "RequestCallback successful for transaction:",
        jwtSession.transactionId
      );
    } catch (e) {
      console.error("RequestCallbackFailed", e);
      throw e;
    }
  },

  // getConsent method removed - consent text is hardcoded in component
  // Complex BDMS logic moved to backend utility

  // Set insurance consent (simplified for ActiveSession insurance consent flow)
  setInsuranceConsent: async (input: {
    transactionId: number;
    patientUseInsurance: boolean;
  }) => {
    try {
      const url =
        process.env.REACT_APP_BASE_URL_V2 + "/Transaction/SetInsuranceConsent";
      const response = await axios.post(
        url,
        {
          transactionId: input.transactionId,
          patientUseInsurance: input.patientUseInsurance,
        },
        {
          headers: transactionService.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (e) {
      console.error("SetInsuranceConsentFailed", e);
      throw e;
    }
  },

  // Set consent.
  setConsent: async (input: ISetConsentInput) => {
    try {
      const url = process.env.REACT_APP_BASE_URL_V2 + "/Transaction/SetConsent";
      const response = await axios.post(
        url,
        {
          buCode: input.buCode,
          consent: input.consent,
        },
        {
          headers: transactionService.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (e) {
      console.error("SetConsentFailed", e);
      return undefined;
    }
  },

  // recordDiagnosticEvent method removed - backend endpoint no longer exists

  // Join URL management with encapsulation
  setJoinUrl: (url: string) => {
    localStorage.setItem("callJoinUrl", url);
    localStorage.setItem("callJoinUrlTimestamp", Date.now().toString());
    console.log("Join URL saved to localStorage");
  },

  getJoinUrl: (): string | null => {
    const url = localStorage.getItem("callJoinUrl");
    if (!url) return null;

    // Validate timestamp (24 hour expiry)
    const timestamp = localStorage.getItem("callJoinUrlTimestamp");
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age > 24 * 60 * 60 * 1000) {
        console.log("Join URL expired (>24 hours), clearing");
        transactionService.clearJoinUrl();
        return null;
      }
    }
    return url;
  },

  clearJoinUrl: () => {
    localStorage.removeItem("callJoinUrl");
    localStorage.removeItem("callJoinUrlTimestamp");
    console.log("Join URL cleared from localStorage");
  },

  isJoinUrlValid: (): boolean => {
    const url = transactionService.getJoinUrl();
    return url !== null && url !== "";
  },
};

export {
  transactionService,
  JwtSession,
  WebSocketQueueData,
  CheckQueueApiResponse,
};

interface ISetConsentInput {
  buCode: string[];
  consent: {
    consentNumber: string;
    flag: boolean;
  }[];
}
